/**
 * M5 — dates. A birthday or anniversary within 7 days. Row when it is 3–7
 * days out (class 3), card within 2 days (class 1) — the payload's density
 * decides. Variants: upcoming, today, passed while away (S4, softer copy).
 *
 * The primary tap (and "draft a message") is `open`: the host's route map
 * prefills a note to this person through f8. "Plan with the circle" is
 * `open` via 'circle' and appears only when the person shares a circle.
 *
 * No age unless the server sent one (dobYear known); no pronouns — the name
 * is used again instead.
 */

import { Gift } from 'react-feather';
import { HomeAction, HomeActions, HomeCard, HomeGlyph, HomeRow } from '../HomeKit';
import { dayLabel, possessive } from '../format';
import type { ModuleProps } from '../moduleTypes';

function onDay(date: string): string {
  const day = dayLabel(date);
  return /^(today|tomorrow|yesterday)$/.test(day) || !day ? day : `on ${day}`;
}

export function DatesModule({ module, onAction }: ModuleProps) {
  if (module.type !== 'dates') return null;
  const { payload } = module;
  const act = (a: Parameters<NonNullable<typeof onAction>>[0]) => onAction?.(a, module);
  const name = payload.personName;
  const what = payload.kind === 'anniversary' ? 'anniversary' : 'birthday';
  const when = onDay(payload.date);
  // "turns N" is a birthday sentence; an age on any other kind is left unsaid rather than misread.
  const age = payload.kind === 'birthday' && payload.age != null ? payload.age : null;

  let body: string;
  if (payload.passedWhileAway) body = `${possessive(name)} ${what} was ${when}`;
  else if (payload.isToday) body = age != null ? `${name} turns ${age} today` : `${possessive(name)} ${what} is today`;
  else body = age != null ? `${name} turns ${age} ${when}` : `${possessive(name)} ${what} is ${when}`;

  const circle = payload.inSharedCircle && payload.circleId ? payload : null;
  const sub = payload.passedWhileAway ? 'a late note still counts' : circle?.circleName ? `in ${circle.circleName} with you` : undefined;

  const draft = () => act({ kind: 'open' });
  const dismiss = module.dismissable ? () => act({ kind: 'dismiss', dismissKey: module.dismissKey }) : undefined;
  const plan = circle ? () => act({ kind: 'open', via: 'circle' }) : null;

  if (module.density === 'row') {
    return (
      <HomeRow
        reason={module.reason}
        body={body}
        sub={sub}
        glyph={
          <HomeGlyph tone="gold">
            <Gift size={13} />
          </HomeGlyph>
        }
        dismissable={module.dismissable}
        onDismiss={dismiss}
        onOpen={draft}
      />
    );
  }

  return (
    <HomeCard
      reason={module.reason}
      body={body}
      sub={sub}
      dismissable={module.dismissable}
      onDismiss={dismiss}
      onOpen={draft}
      secondary={
        <HomeActions>
          <HomeAction primary onClick={draft}>draft a message</HomeAction>
          {plan ? <HomeAction onClick={plan}>plan with the circle</HomeAction> : null}
        </HomeActions>
      }
    />
  );
}
